import moment from 'moment';
import { DATE_FORMAT } from 'framework/utils';
import { ERROR_MESSAGES, PERIOD_POLICY } from '../constant';
import {
  BillingPeriodItems,
  BillingPeriodType,
  BillingMilestone,
  BillingChangeRequestItem,
  CalendarMonthPeriod,
  CustomPeriod,
} from '../types';
import { BillingType } from 'domain/shared/newProject';
import { MILESTONES_ALIAS } from '../../milestones/useMilestonesService';
import { FormFieldsData } from 'framework/form';
import { HTTP } from 'framework/api';
import {
  BillingScheduleItemResponse,
  BillingScheduleResponse,
  BillingScheduleSensitiveItem,
} from '../../finance/billing/billingTypes';

// Default data
export const DEFAULT_MILESTONE_STRUCT = [
  {
    milestoneName: 'Contract signed',
    milestoneType: MILESTONES_ALIAS.ADVANCED_PAYMENT,
  },
  {
    milestoneName: '25% completion',
    milestoneType: MILESTONES_ALIAS.PROGRESS_BILLING,
  },
  {
    milestoneName: '50% completion',
    milestoneType: MILESTONES_ALIAS.PROGRESS_BILLING,
  },
  {
    milestoneName: '75% completion',
    milestoneType: MILESTONES_ALIAS.PROGRESS_BILLING,
  },
  {
    milestoneName: '100% completion',
    milestoneType: MILESTONES_ALIAS.PROGRESS_BILLING,
  },
  {
    milestoneName: 'Acceptance',
    milestoneType: MILESTONES_ALIAS.ACCEPTANCE_MILESTONE,
  },
];

export const generateBillingPeriods = (
  periodType: BillingPeriodType,
  startDate: string,
  endDate: string,
): BillingPeriodItems[] => {
  const start = moment(startDate);
  const end = moment(endDate);
  if (periodType === PERIOD_POLICY.CALENDAR_MONTH) {
    return generateCalendarMonthPeriods(start, end);
  } else {
    return generateCustomPeriods(start, end);
  }
};

const generateCalendarMonthPeriods = (start: moment.Moment, end: moment.Moment): CalendarMonthPeriod[] => {
  const periods: CalendarMonthPeriod[] = [];
  let current = start.clone().startOf('month');

  while (current.isSameOrBefore(end)) {
    const periodMonth = current.format(DATE_FORMAT.API_ONLY_MONTH);
    const invoiceDate = getFirstBusinessDayOfMonth(current.year(), current.month());

    periods.push({
      periodMonth,
      invoiceDate,
    });

    current = current.add(1, 'month').startOf('month');
  }

  return periods;
};

const generateCustomPeriods = (start: moment.Moment, end: moment.Moment): CustomPeriod[] => {
  const periods: CustomPeriod[] = [];
  let currentStart = start.clone();

  while (currentStart.isSameOrBefore(end)) {
    // End date - one month from the start date (minus 1 day, since the period is inclusive)
    const currentEnd = currentStart.clone().add(1, 'month').subtract(1, 'day');

    // If currentEnd goes beyond the common end, use the common end
    const endDate = currentEnd.isAfter(end) ? end.clone() : currentEnd;

    // Invoice date - the first business day after endDate
    const invoiceDateMoment = endDate.clone().add(1, 'day');
    const invoiceDate = getFirstBusinessDay(invoiceDateMoment);

    const safeInvoiceDate = moment(invoiceDate).isAfter(end) ? end.format(DATE_FORMAT.API_ONLY_DATE) : invoiceDate;

    periods.push({
      startDate: currentStart.format(DATE_FORMAT.API_ONLY_DATE),
      endDate: endDate.format(DATE_FORMAT.API_ONLY_DATE),
      invoiceDate: safeInvoiceDate,
    });

    // The next period starts on the day after endDate
    currentStart = endDate.clone().add(1, 'day');

    // If currentStart goes beyond end, break the loop
    if (currentStart.isAfter(end)) {
      break;
    }
  }

  return periods;
};

const getFirstBusinessDay = (date: moment.Moment): string => {
  let result = date.clone();

  // Skip Saturdays (6) and Sundays (0)
  while (result.day() === 6 || result.day() === 0) {
    result = result.add(1, 'day');
  }

  return result.format(DATE_FORMAT.API_ONLY_DATE);
};

const getFirstBusinessDayOfMonth = (year: number, month: number): string => {
  let firstDay = moment([year, month, 1]);

  // Check if it's a day off (Saturday = 6, Sunday = 0)
  while (firstDay.day() === 6 || firstDay.day() === 0) {
    firstDay = firstDay.add(1, 'day');
  }

  return firstDay.format(DATE_FORMAT.API_ONLY_DATE);
};

// Validation
export const getDuplicatePeriodMonthErrors = (scheduleItems: CalendarMonthPeriod[]): number[] => {
  const periodMonthMap = new Map<string, number[]>();
  scheduleItems.forEach((item, index) => {
    const periodMonth = item.periodMonth;
    if (!periodMonthMap.has(periodMonth)) {
      periodMonthMap.set(periodMonth, []);
    }
    periodMonthMap.get(periodMonth)!.push(index);
  });

  const duplicateIndexes: number[] = [];
  periodMonthMap.forEach((indexes, periodMonth) => {
    if (indexes.length > 1) {
      duplicateIndexes.push(...indexes);
    }
  });

  return duplicateIndexes;
};

export const getOverlappingPeriodErrors = (scheduleItems: CustomPeriod[]): number[] => {
  const overlappingIndexes: number[] = [];

  const periods = scheduleItems.map((item, index) => ({
    index,
    start: moment(item.startDate),
    end: moment(item.endDate),
  }));

  for (let i = 0; i < periods.length; i++) {
    for (let j = i + 1; j < periods.length; j++) {
      const periodA = periods[i];
      const periodB = periods[j];

      const isOverlapping =
        periodA.start.isBetween(periodB.start, periodB.end, null, '[]') ||
        periodA.end.isBetween(periodB.start, periodB.end, null, '[]') ||
        periodB.start.isBetween(periodA.start, periodA.end, null, '[]') ||
        periodB.end.isBetween(periodA.start, periodA.end, null, '[]');

      if (isOverlapping) {
        if (!overlappingIndexes.includes(periodA.index)) {
          overlappingIndexes.push(periodA.index);
        }
        if (!overlappingIndexes.includes(periodB.index)) {
          overlappingIndexes.push(periodB.index);
        }
      }
    }
  }

  return overlappingIndexes;
};

// Helper functions
export const createErrorResponse = (errors: Record<string, string | undefined>) => {
  return new Response(JSON.stringify({ errors }), {
    status: HTTP.BAD_REQUEST,
    headers: { 'Content-Type': 'application/json' },
  });
};

// Validate form data (scheduleItems)
export const validateFormData = (data: FormFieldsData) => {
  const isMilestone = data.billingType === BillingType.MILESTONE;
  const periodPolicy = data.periodPolicy;
  const nonChangeRequestItems = (data.scheduleItems as BillingPeriodItems[]).filter(item => {
    return item.itemType !== 'CHANGE_REQUEST' && !item.isSowStartCarryover;
  });

  let calendarErrors: number[] = [];
  let customErrors: number[] = [];

  if (!isMilestone) {
    if (periodPolicy === PERIOD_POLICY.CALENDAR_MONTH) {
      calendarErrors = getDuplicatePeriodMonthErrors(nonChangeRequestItems as CalendarMonthPeriod[]);
    } else if (periodPolicy === PERIOD_POLICY.CUSTOM_PERIOD) {
      customErrors = getOverlappingPeriodErrors(nonChangeRequestItems as CustomPeriod[]);
    }
  }

  return { calendarErrors, customErrors };
};

const normalizeMoney = (value: number): number => Number(value.toFixed(2));

export const getBillingScheduleItemsTotalAmount = (scheduleItems: BillingPeriodItems[] = []): number =>
  normalizeMoney(
    scheduleItems.reduce((acc, item) => {
      return acc + (item.itemType === 'CHANGE_REQUEST' ? 0 : Number(item.amount) || 0);
    }, 0),
  );

export const getBillingScheduleTargetAmount = (budgetAmount?: number | string | null): number =>
  normalizeMoney(Number(budgetAmount) || 0);

// Constructing an error object
export const buildErrorObject = (
  calendarErrors: number[],
  customErrors: number[],
): Record<string, string | undefined> => {
  const errors: Record<string, string | undefined> = {};

  // PeriodMonth duplication errors
  calendarErrors.forEach(errorIndex => {
    errors[`scheduleItems.${errorIndex}.periodMonth`] = ERROR_MESSAGES.DUPLICATE_PERIOD_MONTH;
  });

  // Sum error
  customErrors.forEach(errorIndex => {
    errors[`scheduleItems.${errorIndex}.startDate`] = ERROR_MESSAGES.OVERLAPPING_PERIODS;
    errors[`scheduleItems.${errorIndex}.endDate`] = ERROR_MESSAGES.OVERLAPPING_PERIODS;
  });

  return errors;
};

const sortByPosition = (a: BillingScheduleItemResponse, b: BillingScheduleItemResponse): number => {
  const left = (a.position || '')
    .split('.')
    .map(Number)
    .filter(value => !Number.isNaN(value));
  const right = (b.position || '')
    .split('.')
    .map(Number)
    .filter(value => !Number.isNaN(value));
  const maxLength = Math.max(left.length, right.length);

  for (let index = 0; index < maxLength; index += 1) {
    const leftValue = left[index] ?? 0;
    const rightValue = right[index] ?? 0;

    if (leftValue !== rightValue) {
      return leftValue - rightValue;
    }
  }

  return 0;
};

const getScheduleItemMatchKey = (item: BillingPeriodItems | BillingScheduleItemResponse): string | null => {
  if ('periodMonth' in item && item.periodMonth) {
    return `calendar:${item.periodMonth}`;
  }

  if ('startDate' in item && 'endDate' in item && item.startDate && item.endDate) {
    return `custom:${item.startDate}:${item.endDate}`;
  }

  if ('alias' in item && item.alias) {
    return `milestone-alias:${item.alias}`;
  }

  if ('milestoneType' in item && 'milestoneName' in item && item.milestoneType && item.milestoneName) {
    return `milestone:${item.milestoneType}:${item.milestoneName}`;
  }

  return null;
};

const hasScheduleItemMatchKey = (key: string | null): key is string => key !== null;

const getScheduleItemStartDate = (item: BillingPeriodItems | BillingScheduleItemResponse): string | null => {
  if ('periodMonth' in item && item.periodMonth) {
    return `${item.periodMonth}-01`;
  }

  if ('startDate' in item && item.startDate) {
    return item.startDate;
  }

  return null;
};

const mapScheduleItemToFormItem = (
  item: BillingScheduleItemResponse,
  sensitiveItem?: BillingScheduleSensitiveItem,
  hasRelatedChangeRequests = false,
): BillingPeriodItems | null => {
  const meta = {
    id: item.id,
    sowId: item.sowId,
    billingRequestId: 'billingRequestId' in item ? (item.billingRequestId ?? null) : null,
    hours: item.hours ?? null,
    position: item.position,
    status: item.status,
    amount: `${sensitiveItem?.amount ?? item.amount ?? ''}`,
    itemType: item.type,
    hasRelatedChangeRequests,
  };

  if (item.type === 'CHANGE_REQUEST') {
    return {
      ...meta,
      changeRequestAlias: 'changeRequestAlias' in item ? item.changeRequestAlias : undefined,
      invoiceDate: sensitiveItem?.invoiceDate ?? item.invoiceDate ?? null,
      relatedItem: 'relatedItem' in item ? (item.relatedItem ?? null) : null,
    } as BillingChangeRequestItem;
  }

  if ('periodMonth' in item && item.periodMonth) {
    return {
      ...meta,
      periodMonth: item.periodMonth,
      invoiceDate: sensitiveItem?.invoiceDate ?? item.invoiceDate ?? '',
    } as CalendarMonthPeriod;
  }

  if ('startDate' in item && 'endDate' in item && item.startDate && item.endDate) {
    return {
      ...meta,
      startDate: item.startDate,
      endDate: item.endDate,
      invoiceDate: sensitiveItem?.invoiceDate ?? item.invoiceDate ?? '',
    } as CustomPeriod;
  }

  if ('milestoneType' in item && 'milestoneName' in item) {
    return {
      ...meta,
      milestoneType: item.milestoneType,
      milestoneName: item.milestoneName,
      alias: item.alias,
      invoiceDate: sensitiveItem?.invoiceDate ?? item.invoiceDate ?? null,
      isCompleted: item.isCompleted,
    } as BillingMilestone;
  }

  return null;
};

const mapExistingScheduleItems = (
  schedule: BillingScheduleResponse,
  sensitiveItemsMap: Map<string, BillingScheduleSensitiveItem>,
): BillingPeriodItems[] => {
  const relatedChangeRequestIds = new Set(
    (schedule.scheduleItems ?? [])
      .filter(
        (item): item is BillingScheduleItemResponse & { type: 'CHANGE_REQUEST'; relatedItem?: string } =>
          item.type === 'CHANGE_REQUEST' && Boolean('relatedItem' in item ? item.relatedItem : null),
      )
      .map(item => item.relatedItem!)
      .filter(Boolean),
  );

  return [...(schedule.scheduleItems ?? [])]
    .sort(sortByPosition)
    .map(item => mapScheduleItemToFormItem(item, sensitiveItemsMap.get(item.id), relatedChangeRequestIds.has(item.id)))
    .filter((item): item is BillingPeriodItems => Boolean(item));
};

export const getExistingBillingScheduleItems = (
  schedule: BillingScheduleResponse,
  sensitiveItemsMap: Map<string, BillingScheduleSensitiveItem> = new Map(),
): BillingPeriodItems[] => mapExistingScheduleItems(schedule, sensitiveItemsMap);

const canApplyCurrentSowToItem = (item: BillingPeriodItems): boolean => {
  if (item.billingRequestId) {
    return false;
  }

  if (item.hasRelatedChangeRequests) {
    return false;
  }

  if ('isCompleted' in item && item.isCompleted) {
    return false;
  }

  return item.itemType !== 'CHANGE_REQUEST';
};

const applyCurrentSowToUnlockedItems = (items: BillingPeriodItems[], currentSowId?: string): BillingPeriodItems[] => {
  if (!currentSowId) {
    return items;
  }

  return items.map(item => (canApplyCurrentSowToItem(item) ? { ...item, sowId: currentSowId } : item));
};

const shouldKeepExistingBillingScheduleItem = (item: BillingPeriodItems, generatedKeys: Set<string>): boolean => {
  const key = getScheduleItemMatchKey(item);

  if (!key) {
    return !isBillingScheduleItemRemovable(item);
  }

  return generatedKeys.has(key) || !isBillingScheduleItemRemovable(item);
};

const filterGeneratedItemsBeforeEarliestLinkedBillingRequest = (
  existingItems: BillingPeriodItems[],
  generatedItems: BillingPeriodItems[],
): BillingPeriodItems[] => {
  const earliestLinkedBillingRequestStartDate = existingItems
    .filter(item => Boolean(item.billingRequestId))
    .map(getScheduleItemStartDate)
    .filter((startDate): startDate is string => Boolean(startDate))
    .sort()[0];

  if (!earliestLinkedBillingRequestStartDate) {
    return generatedItems;
  }

  return generatedItems.filter(item => {
    const itemStartDate = getScheduleItemStartDate(item);

    return !itemStartDate || itemStartDate >= earliestLinkedBillingRequestStartDate;
  });
};

const getSowStartCarryoverKeys = ({
  currentSowStartDate,
  existingItems,
  generatedKeys,
}: {
  currentSowStartDate?: string;
  existingItems: BillingPeriodItems[];
  generatedKeys: Set<string>;
}): Set<string> => {
  if (!currentSowStartDate) {
    return new Set();
  }

  return new Set(
    existingItems
      .filter(item => Boolean(item.billingRequestId) && getScheduleItemStartDate(item) === currentSowStartDate)
      .map(getScheduleItemMatchKey)
      .filter(hasScheduleItemMatchKey)
      .filter(key => generatedKeys.has(key)),
  );
};

export const mergeBillingScheduleItems = ({
  currentSowId,
  currentSowStartDate,
  existingItems,
  generatedItems,
}: {
  currentSowId?: string;
  currentSowStartDate?: string;
  existingItems: BillingPeriodItems[];
  generatedItems: BillingPeriodItems[];
}): BillingPeriodItems[] => {
  const filteredGeneratedItems = filterGeneratedItemsBeforeEarliestLinkedBillingRequest(existingItems, generatedItems);
  const generatedKeys = new Set(filteredGeneratedItems.map(getScheduleItemMatchKey).filter(hasScheduleItemMatchKey));
  const sowStartCarryoverKeys = getSowStartCarryoverKeys({
    currentSowStartDate,
    existingItems,
    generatedKeys,
  });
  const normalizedExistingItems = applyCurrentSowToUnlockedItems(
    existingItems.filter(item => shouldKeepExistingBillingScheduleItem(item, generatedKeys)),
    currentSowId,
  ).map(item => {
    const key = getScheduleItemMatchKey(item);

    if (
      !currentSowStartDate ||
      !key ||
      !sowStartCarryoverKeys.has(key) ||
      getScheduleItemStartDate(item) !== currentSowStartDate ||
      !item.billingRequestId
    ) {
      return item;
    }

    return {
      ...item,
      isSowStartCarryover: true,
    };
  });
  const existingKeys = new Set(normalizedExistingItems.map(getScheduleItemMatchKey).filter(hasScheduleItemMatchKey));
  const newItems = filteredGeneratedItems
    .filter(item => {
      const key = getScheduleItemMatchKey(item);
      return !key || !existingKeys.has(key);
    })
    .map(item => ({
      ...item,
      sowId: item.sowId ?? currentSowId,
    }));

  return [...normalizedExistingItems, ...newItems];
};

export const getBillingScheduleFormItems = ({
  currentSowId,
  currentSowStartDate,
  generatedItems,
  schedule,
  sensitiveItemsMap,
}: {
  currentSowId?: string;
  currentSowStartDate?: string;
  generatedItems: BillingPeriodItems[];
  schedule?: BillingScheduleResponse;
  sensitiveItemsMap?: Map<string, BillingScheduleSensitiveItem>;
}): BillingPeriodItems[] => {
  if (!schedule) {
    return generatedItems.map(item => ({
      ...item,
      sowId: item.sowId ?? currentSowId,
    }));
  }

  const existingItems = getExistingBillingScheduleItems(schedule, sensitiveItemsMap ?? new Map());

  return mergeBillingScheduleItems({
    currentSowId,
    currentSowStartDate,
    existingItems,
    generatedItems,
  });
};

export const hasLockedBillingScheduleItems = (items: BillingPeriodItems[]): boolean =>
  items.some(item => Boolean(item.billingRequestId));

export const isBillingScheduleItemLocked = (item?: BillingPeriodItems | null): boolean =>
  Boolean(item?.billingRequestId);

const isChangeRequestBillingItem = (item?: BillingPeriodItems | null): item is BillingChangeRequestItem =>
  Boolean(item?.itemType === 'CHANGE_REQUEST');

const isCompletedBillingMilestone = (item?: BillingPeriodItems | null): item is BillingMilestone =>
  Boolean(item && 'isCompleted' in item && item.isCompleted);

export const isBillingScheduleItemStructureLocked = (item?: BillingPeriodItems | null): boolean =>
  Boolean(item?.billingRequestId || isCompletedBillingMilestone(item) || isChangeRequestBillingItem(item));

export const isBillingScheduleItemFullyLocked = (item?: BillingPeriodItems | null): boolean =>
  Boolean(isCompletedBillingMilestone(item) || isChangeRequestBillingItem(item));

export const isBillingScheduleItemRemovable = (item?: BillingPeriodItems | null): boolean =>
  Boolean(item) &&
  !item?.billingRequestId &&
  !isBillingScheduleItemFullyLocked(item) &&
  !item?.hasRelatedChangeRequests;

export const getBillingScheduleItemRemoveTooltip = (item?: BillingPeriodItems | null): string =>
  isBillingScheduleItemLocked(item)
    ? 'Items with initialized billing requests cannot be removed from the schedule or have their structure changed. Their amount, and invoice date for custom periods, can still be updated.'
    : item?.hasRelatedChangeRequests
      ? 'Items with related change requests cannot be removed or updated in the schedule.'
      : isChangeRequestBillingItem(item)
        ? 'Change request items cannot be removed or updated in the schedule.'
        : isCompletedBillingMilestone(item)
          ? 'Completed milestone items cannot be removed or updated in the schedule.'
          : '';
